import React, { useState, useEffect } from "react";
import { Card, List, Button, message, Alert } from "antd";
import { useAuth } from "../../../../context/authContext";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";
import useReportService from "../../../../services/reportService";

const KISuggestions = () => {
  const { user, fetchUser } = useAuth();
  const { kiSuggestions: usage } = user.reportUsage;
  const { kiSuggestions: limit } = user.reportLimits;
  const { getKISuggestions, getSavedKISuggestions } = useReportService();
  const limitReached = usage >= limit;
  const { language } = useLanguage();
  const text = dashboardContent[language].reports.analysis;

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canFetch, setCanFetch] = useState(true);

  const fetchNewSuggestions = async () => {
    if (!canFetch) {
      message.warning(text.kiFetchWarning);
      return;
    }

    try {
      setLoading(true);
      const newSuggestions = await getKISuggestions({ language });

      if (!newSuggestions || newSuggestions.length === 0) {
        message.info(
          text.kiNoSuggestions || "Keine neuen Vorschläge gefunden."
        );
        return;
      }

      // Optional: Vorschläge formatieren (falls nicht schon im richtigen Format)
      const formatted = newSuggestions.map((desc, idx) => ({
        key: `${idx + 1}`,
        title: `${text.kiSuggestionPrefix || "Vorschlag"} ${idx + 1}`,
        description: desc,
      }));

      setSuggestions(formatted);
      setCanFetch(false);
      message.success(text.kiFetchSuccess);
    } catch (err) {
      console.error("Fehler beim Laden der KI-Vorschläge:", err);
      message.error("Fehler beim Abrufen der KI-Vorschläge.");
    } finally {
      setLoading(false);
      fetchUser();
    }
  };

  const loadSavedSuggestions = async () => {
    try {
      const savedSuggestions = await getSavedKISuggestions();
      if (!savedSuggestions.length) return;

      const formatted = savedSuggestions.map((desc, idx) => ({
        key: `${idx + 1}`,
        title: `${text.kiSuggestionPrefix || "Vorschlag"} ${idx + 1}`,
        description: desc,
      }));

      setSuggestions(formatted);
      setCanFetch(true); // Kein erneutes Abholen möglich
    } catch (err) {
      console.error("Fehler beim Laden gespeicherter Vorschläge:", err);
    }
  };

  useEffect(() => {
    loadSavedSuggestions();
  }, []);

  return (
    <Card
      title={`${text.kiSuggestionsTitle} ${usage}/${limit}`}
      className="mb-6 shadow-md"
    >
      <Alert message={text.kiFetchInfo} type="info" showIcon className="mb-4" />

      <List
        itemLayout="vertical"
        dataSource={suggestions}
        locale={{
          emptyText: text.kiNoSuggestions || "Keine Vorschläge vorhanden.",
        }}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta
              title={<strong>{item.title}</strong>}
              description={item.description}
            />
          </List.Item>
        )}
      />

      <div className="flex justify-center mt-4">
        <Button
          type="primary"
          className="bg-orange-500 !hover:bg-orange-600"
          onClick={fetchNewSuggestions}
          loading={loading}
          disabled={limitReached || !canFetch}
        >
          {text.kiFetchButton}
        </Button>
      </div>
    </Card>
  );
};

export default KISuggestions;
